'use client'

import { motion } from 'motion/react'
import { useCallback } from 'react'
import { prepareTagSlugs } from '@/api/restaurants/get-restaurants-query'
import { ITag } from '@/api/restaurants/types/restaurant-types'
import { useGetAllTagsQuery } from '@/api/tags/get-all-tags-query'
import { useRestaurantCitySearchContext } from '@/features/restaurants/restaurants-city-search/utils/restaurant-city-search-context'
import { cn } from '@/utils/styles/cn'

/**
 * Emoji category circles with labels, horizontally scrollable, tags stay in
 * place when selected (marked with a ring) instead of jumping to a second row.
 */
export const RestaurantCitySearchCategoryRow = () => {
    const {
        query,
        setters: { setTagSlugs },
    } = useRestaurantCitySearchContext()

    const { data } = useGetAllTagsQuery(query.citySlug ?? undefined)

    const tags = data?.data

    // Single select: while browsing you click through the categories, selecting
    // one replaces the previous, clicking the active one clears the filter
    const toggleTag = useCallback(
        (tag: ITag) => {
            const isSelected = prepareTagSlugs(query.tagSlugs)?.includes(tag.slug)

            setTagSlugs(isSelected ? [] : [tag.slug])
        },
        [query.tagSlugs, setTagSlugs]
    )

    if (!tags?.length) return null

    return (
        <div
            className="flex gap-x-1 md:gap-x-2 overflow-x-auto scrollbar-hide -mx-4 w-[calc(100%+32px)] px-4
                md:mx-0 md:w-full md:px-0"
        >
            {tags.map((tag) => {
                const isSelected = Boolean(query.tagSlugs?.includes(tag.slug))

                return (
                    <button
                        type="button"
                        key={'category-row' + tag.slug}
                        onClick={() => toggleTag(tag)}
                        className="flex flex-col items-center gap-y-1.5 w-[72px] md:w-20 shrink-0 py-1 cursor-pointer
                            select-none webkit-tap-transparent group"
                    >
                        <span
                            className={cn(
                                `flex size-14 md:size-16 items-center justify-center rounded-full
                                bg-bg-neutral-surface text-[26px] md:text-[30px] transition-all duration-150`,
                                isSelected
                                    ? 'ring-2 ring-fg-primary'
                                    : 'group-hover:bg-bg-neutral-surface-medium'
                            )}
                        >
                            <motion.span
                                animate={{
                                    scale: isSelected ? 1.35 : 1,
                                    rotate: isSelected ? -10 : 0,
                                }}
                                transition={{ type: 'spring', stiffness: 450, damping: 12 }}
                                className="flex"
                            >
                                {tag.emoji ?? '🍽️'}
                            </motion.span>
                        </span>

                        <span
                            className={cn(
                                'text-label-sm whitespace-nowrap max-w-full overflow-hidden text-ellipsis',
                                isSelected ? 'text-fg-primary' : 'text-fg-secondary'
                            )}
                        >
                            {tag.name}
                        </span>
                    </button>
                )
            })}
        </div>
    )
}
